import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header.js';
import ViewportTracker from './ViewportTracker.js';
import NewsSubscribeButton from './NewsSubscribeButton.js';
import { PAGE_MAX_WIDTH, PAGE_CONTAINER_MARK } from './layout.js';

const typeLabel = { social: 'Social', blog: 'Blog', update: 'Update' };
const typeColor = { social: '#5a8ab8', blog: '#6a9e7f', update: '#b87a5a' };

// localStorage keys for the anonymous-like UX. The server does NOT receive
// either of these values — they exist only so a guest's heart stays red on
// reload and they can't click the same post twice in one browser session.
// Abuse is capped server-side by the IP rate limiter; clearing storage to
// like again is accepted, not defended against, by design.
export const ANON_ID_KEY = 'atw_anon_id';
export const LIKED_POSTS_KEY = 'atw_liked_posts';

// Lazily mints and caches a per-browser anon identifier. Not currently sent
// to the server (one-way anon likes deliberately store no per-user identity
// server-side), but kept so future client-side features can correlate
// behavior without standing up new collections.
export const getAnonId = () => {
    try {
        let id = localStorage.getItem(ANON_ID_KEY);
        if (!id) {
            id = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
                ? crypto.randomUUID()
                : `anon-${Date.now()}-${Math.random().toString(36).slice(2)}`;
            localStorage.setItem(ANON_ID_KEY, id);
        }
        return id;
    } catch {
        return null;
    }
};

export const loadLikedPosts = () => {
    try {
        const raw = localStorage.getItem(LIKED_POSTS_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : [];
    } catch {
        return [];
    }
};

export const hasLikedPost = (postId) => loadLikedPosts().includes(postId);

export const markPostLiked = (postId) => {
    try {
        const current = loadLikedPosts();
        if (current.includes(postId)) return current;
        const updated = [...current, postId];
        localStorage.setItem(LIKED_POSTS_KEY, JSON.stringify(updated));
        return updated;
    } catch {
        return loadLikedPosts();
    }
};

// Build a PATCH /news/:id body from the edit-form state. Tags is a
// comma-separated string in the form UI; convert to an array. Empty optional
// fields become null (matches the create form's handling in NewsAdmin).
export const buildEditBody = (form) => ({
    title: form.title,
    summary: form.summary,
    description: form.description,
    author: form.author,
    type: form.type,
    tags: typeof form.tags === 'string'
        ? form.tags.split(',').map((t) => t.trim()).filter(Boolean)
        : (Array.isArray(form.tags) ? form.tags : []),
    image: form.image || null,
    link: form.link || null,
    sourceUrl: form.sourceUrl || null,
    sourcePlatform: form.sourcePlatform || null,
});

// Returns true if the logged-in user is allowed to see admin controls on
// individual post cards. Pulled out so a unit test can pin the rule.
export const canManagePosts = (loggedInUser) => loggedInUser?.role === 'ADMIN';

export const NewsCard = ({ post, loggedInUser, onEdit, onDelete, onPin }) => {
    const [likes, setLikes] = useState(post.likes);
    // Logged-out users see the heart pre-filled if this browser already liked
    // the post in a previous session. Logged-in users start at false because
    // the per-user toggle state isn't sent down with the post list — kept the
    // existing behavior to avoid a separate /me fetch on every page load.
    const [liked, setLiked] = useState(() => !loggedInUser && hasLikedPost(post._id));

    const handleLike = async () => {
        if (loggedInUser) {
            const token = localStorage.getItem('token');
            const res = await fetch(`https://vicktoria.ai/api/news/${post._id}/like`, {
                method: 'PATCH',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            setLikes(data.likes);
            setLiked(data.liked);
            return;
        }

        // Anonymous path: one-way like. Bail early if this browser already
        // liked the post so we don't double-fire on a double click.
        if (hasLikedPost(post._id)) return;
        getAnonId();
        try {
            const res = await fetch(`https://vicktoria.ai/api/news/${post._id}/like-anon`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });
            if (!res.ok) return;
            const data = await res.json();
            setLikes(data.likes);
            setLiked(true);
            markPostLiked(post._id);
        } catch {
            // Network errors are silent here — the user can retry. We don't
            // want a guest like to surface scary errors on a marketing page.
        }
    };

    return (
        <div style={{
            backgroundColor: '#81B2CA',
            borderRadius: '20px',
            padding: 'clamp(16px, 3vw, 30px)',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            position: 'relative'
        }}>

            {/* Pin badge */}
            {post.isPinned && (
                <div style={{
                    position: 'absolute', top: '14px', right: '14px',
                    backgroundColor: '#4a7094ff', color: '#fff',
                    borderRadius: '8px', padding: '2px 10px',
                    fontSize: '12px', fontFamily: 'Inter, sans-serif', fontWeight: '600'
                }}>📌 Pinned</div>
            )}

            {/* Type badge */}
            <div style={{
                display: 'inline-block',
                backgroundColor: typeColor[post.type] || '#4a7094ff',
                color: '#fff', borderRadius: '8px',
                padding: '2px 12px', fontSize: '12px',
                fontFamily: 'Inter, sans-serif', fontWeight: '600',
                alignSelf: 'flex-start'
            }}>
                {typeLabel[post.type] || post.type}
            </div>

            {/* Title */}
            <h3 style={{ fontFamily: 'Inter, sans-serif', fontSize: 'clamp(18px, 2vw, 22px)', margin: 0, color: '#000' }}>
                {post.title}
            </h3>

            {/* Image */}
            {post.image && (
                <img src={post.image} alt={post.title} style={{
                    width: '100%', borderRadius: '12px', objectFit: 'cover', maxHeight: '300px'
                }} />
            )}

            {/* Social media embed */}
            {post.type === 'social' && post.sourceUrl && (
                <div style={{
                    backgroundColor: '#B3CFDB', borderRadius: '12px', padding: '12px',
                    fontFamily: 'Inter, sans-serif', fontSize: '14px'
                }}>
                    <a href={post.sourceUrl} target="_blank" rel="noopener noreferrer"
                        style={{ color: '#4a7094ff', fontWeight: '600', textDecoration: 'none' }}>
                        🔗 View original post on {post.sourcePlatform || 'social media'}
                    </a>
                    {post.sourceUrl && (
                        <div style={{ marginTop: '8px' }}>
                            <a href={post.sourceUrl} target="_blank" rel="noopener noreferrer"
                                style={{
                                    backgroundColor: '#4a7094ff', color: '#fff', borderRadius: '8px',
                                    padding: '4px 14px', fontSize: '12px', textDecoration: 'none',
                                    fontWeight: '600'
                                }}>
                                + Follow
                            </a>
                        </div>
                    )}
                </div>
            )}

            {/* Summary */}
            <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '15px', color: '#000', margin: 0, lineHeight: '1.6' }}>
                {post.summary}
            </p>

            {/* Description */}
            {post.description && (
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#222', margin: 0, lineHeight: '1.6' }}>
                    {post.description}
                </p>
            )}

            {/* Link */}
            {post.link && (
                <a href={post.link} target="_blank" rel="noopener noreferrer"
                    style={{ color: '#4a7094ff', fontFamily: 'Inter, sans-serif', fontSize: '14px', fontWeight: '600' }}>
                    Read more →
                </a>
            )}

            {/* Tags */}
            {post.tags?.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {post.tags.map((tag, i) => (
                        <span key={i} style={{
                            backgroundColor: '#B3CFDB', borderRadius: '20px',
                            padding: '2px 12px', fontSize: '12px',
                            fontFamily: 'Inter, sans-serif', color: '#000'
                        }}>#{tag}</span>
                    ))}
                </div>
            )}

            {/* Footer: author + date + likes */}
            <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#333',
                borderTop: '1px solid #a0bfce', paddingTop: '10px', marginTop: '4px'
            }}>
                <span>By <strong>{post.author}</strong> · {new Date(post.createdAt).toLocaleDateString()}</span>
                <button
                    onClick={handleLike}
                    disabled={!loggedInUser && liked}
                    aria-label={liked ? 'Already liked' : 'Like this post'}
                    style={{
                        background: 'none', border: 'none',
                        cursor: (!loggedInUser && liked) ? 'default' : 'pointer',
                        fontSize: '15px', color: liked ? '#e05c5c' : '#444',
                        fontFamily: 'Inter, sans-serif', fontWeight: '600'
                    }}>
                    {liked ? '❤️' : '🤍'} {likes}
                </button>
            </div>

            {/* Admin action strip — hidden from non-admins */}
            {canManagePosts(loggedInUser) && (
                <div data-testid="admin-actions" style={{
                    display: 'flex', gap: '8px', justifyContent: 'flex-end',
                    borderTop: '1px solid #a0bfce', paddingTop: '10px'
                }}>
                    <button onClick={() => onEdit(post)} style={{
                        backgroundColor: '#fff', color: '#4a7094ff',
                        border: '1px solid #4a7094ff', borderRadius: '8px',
                        padding: '4px 12px', cursor: 'pointer',
                        fontFamily: 'Inter, sans-serif', fontSize: '13px', fontWeight: '600'
                    }}>Edit</button>
                    <button onClick={() => onPin(post)} style={{
                        backgroundColor: post.isPinned ? '#4a7094ff' : '#fff',
                        color: post.isPinned ? '#fff' : '#4a7094ff',
                        border: '1px solid #4a7094ff', borderRadius: '8px',
                        padding: '4px 12px', cursor: 'pointer',
                        fontFamily: 'Inter, sans-serif', fontSize: '13px', fontWeight: '600'
                    }}>{post.isPinned ? 'Unpin' : 'Pin'}</button>
                    <button onClick={() => onDelete(post)} style={{
                        backgroundColor: '#e05c5c', color: '#fff',
                        border: 'none', borderRadius: '8px',
                        padding: '4px 12px', cursor: 'pointer',
                        fontFamily: 'Inter, sans-serif', fontSize: '13px', fontWeight: '600'
                    }}>Delete</button>
                </div>
            )}
        </div>
    );
};

const EMPTY_EDIT_FORM = {
    title: '', summary: '', description: '', image: '', link: '',
    author: '', type: 'blog', tags: '', sourceUrl: '', sourcePlatform: ''
};

// Modal that prefills with the post being edited. Submits PATCH /news/:id
// and calls onSaved with the updated post so the parent can refresh its list.
const EditModal = ({ post, onClose, onSaved }) => {
    const [form, setForm] = useState(EMPTY_EDIT_FORM);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
        if (!post) return;
        setForm({
            title: post.title || '',
            summary: post.summary || '',
            description: post.description || '',
            image: post.image || '',
            link: post.link || '',
            author: post.author || '',
            type: post.type || 'blog',
            tags: (post.tags || []).join(', '),
            sourceUrl: post.sourceUrl || '',
            sourcePlatform: post.sourcePlatform || '',
        });
        setMessage('');
    }, [post]);

    const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const handleSave = async () => {
        setSaving(true);
        setMessage('');
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`https://vicktoria.ai/api/news/${post._id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(buildEditBody(form)),
            });
            if (res.ok) {
                const updated = await res.json();
                onSaved(updated);
            } else {
                const err = await res.json().catch(() => ({}));
                setMessage(`❌ ${err.error || 'Failed to update post.'}`);
            }
        } catch (err) {
            setMessage('❌ Failed to update post.');
        } finally {
            setSaving(false);
        }
    };

    if (!post) return null;

    const inputStyle = {
        width: '100%', padding: '10px', borderRadius: '8px',
        border: '1px solid #a0bfce', fontFamily: 'Inter, sans-serif',
        fontSize: '14px', boxSizing: 'border-box', backgroundColor: '#fff'
    };
    const labelStyle = {
        fontFamily: 'Inter, sans-serif', fontSize: '13px',
        fontWeight: '600', color: '#000', marginBottom: '4px', display: 'block'
    };

    return (
        <div onClick={onClose} style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
            padding: '40px 20px', zIndex: 1000, overflowY: 'auto'
        }}>
            <div onClick={(e) => e.stopPropagation()} style={{
                backgroundColor: '#81B2CA', borderRadius: '20px', padding: '30px',
                width: '100%', maxWidth: '700px', display: 'flex', flexDirection: 'column', gap: '14px'
            }}>
                <h2 style={{ fontFamily: 'Inter, sans-serif', margin: 0 }}>Edit Post</h2>
                <div><label style={labelStyle}>Title *</label><input style={inputStyle} name="title" value={form.title} onChange={handleChange} /></div>
                <div><label style={labelStyle}>Summary *</label><textarea style={{ ...inputStyle, height: '80px' }} name="summary" value={form.summary} onChange={handleChange} /></div>
                <div><label style={labelStyle}>Description *</label><textarea style={{ ...inputStyle, height: '120px' }} name="description" value={form.description} onChange={handleChange} /></div>
                <div><label style={labelStyle}>Author *</label><input style={inputStyle} name="author" value={form.author} onChange={handleChange} /></div>
                <div>
                    <label style={labelStyle}>Type *</label>
                    <select style={inputStyle} name="type" value={form.type} onChange={handleChange}>
                        <option value="blog">Blog</option>
                        <option value="social">Social</option>
                        <option value="update">Update</option>
                    </select>
                </div>
                <div><label style={labelStyle}>Tags (comma separated)</label><input style={inputStyle} name="tags" value={form.tags} onChange={handleChange} /></div>
                <div><label style={labelStyle}>Image URL</label><input style={inputStyle} name="image" value={form.image} onChange={handleChange} /></div>
                <div><label style={labelStyle}>Link</label><input style={inputStyle} name="link" value={form.link} onChange={handleChange} /></div>
                <div><label style={labelStyle}>Source URL (social only)</label><input style={inputStyle} name="sourceUrl" value={form.sourceUrl} onChange={handleChange} /></div>
                <div>
                    <label style={labelStyle}>Source Platform (social only)</label>
                    <select style={inputStyle} name="sourcePlatform" value={form.sourcePlatform} onChange={handleChange}>
                        <option value="">None</option>
                        <option value="twitter">Twitter</option>
                        <option value="instagram">Instagram</option>
                        <option value="linkedin">LinkedIn</option>
                        <option value="facebook">Facebook</option>
                    </select>
                </div>
                {message && <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', margin: 0 }}>{message}</p>}
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button onClick={onClose} disabled={saving} style={{
                        backgroundColor: '#B3CFDB', color: '#000',
                        border: 'none', borderRadius: '10px', padding: '10px 18px',
                        fontFamily: 'Inter, sans-serif', fontWeight: '600', cursor: 'pointer'
                    }}>Cancel</button>
                    <button onClick={handleSave} disabled={saving} style={{
                        backgroundColor: '#4a7094ff', color: '#fff',
                        border: 'none', borderRadius: '10px', padding: '10px 18px',
                        fontFamily: 'Inter, sans-serif', fontWeight: '600', cursor: 'pointer'
                    }}>{saving ? 'Saving...' : 'Save'}</button>
                </div>
            </div>
        </div>
    );
};

const News = ({ loggedInUser, setLoggedInUser }) => {
    const navigate = useNavigate();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [editingPost, setEditingPost] = useState(null);

    // Pin the filter/subscribe row directly below the sticky header banner.
    // The header height is dynamic (clamp() title, signed-in banner, breakpoints),
    // so we measure the live `.sticky-nav` height instead of hardcoding `top`.
    // When the header's media query drops it to position:relative (mobile), it
    // scrolls away — so we pin the row to top:0 there instead.
    const [stickyTop, setStickyTop] = useState(0);
    // Horizontal margins that pull the filter/Subscribe row into line with the
    // (centered) header: the filter group under the logo's left edge, Subscribe
    // under the Contact Us button's right edge. Measured live because the header
    // centers a variable-width group, so the logo/Contact positions shift with
    // width. On mobile (stacked header) the row just centers and wraps.
    const [toolbar, setToolbar] = useState({ left: 0, right: 0, justify: 'space-between' });
    const colRef = useRef(null);

    useEffect(() => {
        const nav = document.querySelector('.sticky-nav');
        if (!nav) return undefined;

        const measure = () => {
            const isSticky = window.getComputedStyle(nav).position === 'sticky';
            setStickyTop(isSticky ? nav.offsetHeight : 0);

            const isDesktop = typeof window.matchMedia === 'function'
                ? window.matchMedia('(min-width: 601px)').matches : true;
            const logo = nav.querySelector('.logo-slot');
            const btns = nav.querySelectorAll('.nav-btn-responsive');
            const contact = btns[btns.length - 1];
            const col = colRef.current;

            if (!isDesktop || !logo || !contact || !col) {
                // Mobile (or not measurable yet): center + wrap, no side insets.
                const justify = isDesktop ? 'space-between' : 'center';
                setToolbar((p) =>
                    (p.left === 0 && p.right === 0 && p.justify === justify)
                        ? p : { left: 0, right: 0, justify });
                return;
            }

            const logoLeft = logo.getBoundingClientRect().left;      // far-left anchor
            const contactRight = contact.getBoundingClientRect().right; // far-right anchor
            const colRect = col.getBoundingClientRect();
            const cs = window.getComputedStyle(col);
            const contentLeft = colRect.left + parseFloat(cs.paddingLeft || '0');
            const contentRight = colRect.right - parseFloat(cs.paddingRight || '0');
            const left = logoLeft - contentLeft;      // may be negative (header is wider)
            const right = contentRight - contactRight;

            setToolbar((p) =>
                (Math.abs(p.left - left) < 0.5 && Math.abs(p.right - right) < 0.5 && p.justify === 'space-between')
                    ? p : { left, right, justify: 'space-between' });
        };
        measure();

        let ro;
        if (typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(measure);
            ro.observe(nav);
            const logo = nav.querySelector('.logo-slot');
            if (logo) ro.observe(logo); // logo width/position settles after font + sizing
            if (colRef.current) ro.observe(colRef.current);
        }
        window.addEventListener('resize', measure);
        return () => {
            if (ro) ro.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, []);

    const fetchPosts = async () => {
        try {
            const res = await fetch('https://vicktoria.ai/api/news');
            if (!res.ok) throw new Error(`News API returned ${res.status}`);
            const data = await res.json();
            setPosts(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Failed to fetch news:", err);
            setPosts([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchPosts(); }, []);

    const handleEdit = (post) => setEditingPost(post);

    const handleDelete = async (post) => {
        if (!window.confirm(`Delete post "${post.title}"? This cannot be undone.`)) return;
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`https://vicktoria.ai/api/news/${post._id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (res.ok) fetchPosts();
            else alert('Failed to delete post.');
        } catch (err) {
            alert('Failed to delete post.');
        }
    };

    const handlePin = async (post) => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`https://vicktoria.ai/api/news/${post._id}/pin`, {
                method: 'PATCH',
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (res.ok) fetchPosts();
        } catch (err) {
            console.error('Failed to update pin:', err);
        }
    };

    const handleSaved = () => {
        setEditingPost(null);
        fetchPosts();
    };

    const postsArr = Array.isArray(posts) ? posts : [];
    const filtered = filter === 'all' ? postsArr : postsArr.filter(p => p.type === filter);

    const filterBtnStyle = (type) => ({
        backgroundColor: filter === type ? '#4a7094ff' : '#B3CFDB',
        color: filter === type ? '#fff' : '#000',
        border: 'none', borderRadius: '20px',
        padding: '6px 18px', cursor: 'pointer',
        fontFamily: 'Inter, sans-serif', fontWeight: '600', fontSize: '14px'
    });

    return (
        <div className="min-h-screen" style={{ backgroundColor: '#D1DEE6' }}>
            <Header
                loggedInUser={loggedInUser}
                setLoggedInUser={setLoggedInUser}
                topText=""
                midText="Vicktoria"
                botText="News, updates, blog posts, and social media"
            />

            <section style={{ backgroundColor: '#D1DEE6', width: '100%', display: 'flex', justifyContent: 'center', minHeight: 'calc(100vh - 200px)' }}>
                <div ref={colRef} data-layout={PAGE_CONTAINER_MARK} style={{
                    width: '100%', maxWidth: PAGE_MAX_WIDTH,
                    padding: '0 5% 40px 5%', boxSizing: 'border-box',
                    display: 'flex', flexDirection: 'column', gap: '24px'
                }}>

                    {/* Filter buttons (left) + newsletter Subscribe (right).
                        Aligned to the header via measured side margins so the
                        filters sit under the logo's left edge and Subscribe
                        under the Contact Us button's right edge (see `toolbar`
                        in the effect above). Pinned below the sticky header;
                        `top` tracks the live header height. Centers + wraps on
                        mobile. Background + z-index let cards scroll under it. */}
                    <div style={{
                        display: 'flex', justifyContent: toolbar.justify, alignItems: 'center',
                        flexWrap: 'wrap', gap: '10px',
                        position: 'sticky', top: stickyTop, zIndex: 500,
                        backgroundColor: '#D1DEE6',
                        paddingTop: '12px', paddingBottom: '12px',
                        marginLeft: toolbar.left, marginRight: toolbar.right,
                    }}>
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                            {['all', 'social', 'blog', 'update'].map(type => (
                                <button key={type} style={filterBtnStyle(type)} onClick={() => setFilter(type)}>
                                    {type.charAt(0).toUpperCase() + type.slice(1)}
                                </button>
                            ))}
                            {loggedInUser?.role === 'ADMIN' && (
                                <button
                                    onClick={() => navigate('/news-admin')}
                                    style={{
                                        backgroundColor: '#4a7094ff', color: '#fff',
                                        border: 'none', borderRadius: '10px', padding: '10px 20px',
                                        fontFamily: 'Inter, sans-serif', fontWeight: '600',
                                        fontSize: '14px', cursor: 'pointer'
                                    }}>
                                    + Create Post
                                </button>
                            )}
                        </div>
                        <NewsSubscribeButton loggedInUser={loggedInUser} />
                    </div>

                    {/* Posts */}
                    {loading ? (
                        <p style={{ fontFamily: 'Inter, sans-serif', textAlign: 'center', color: '#444' }}>Loading...</p>
                    ) : filtered.length === 0 ? (
                        <p style={{ fontFamily: 'Inter, sans-serif', textAlign: 'center', color: '#444' }}>No posts yet.</p>
                    ) : (
                        filtered.map(post => (
                            <NewsCard
                                key={post._id}
                                post={post}
                                loggedInUser={loggedInUser}
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                                onPin={handlePin}
                            />
                        ))
                    )}

                </div>
            </section>

            <EditModal
                post={editingPost}
                onClose={() => setEditingPost(null)}
                onSaved={handleSaved}
            />

            <ViewportTracker />
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
            <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Judson:wght@400;700&display=swap" rel="stylesheet" />
        </div>
    );
};

export default News;